import { Injectable } from '@angular/core';
import { ApiService } from 'app/core/service/api/api.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AgentFiveService {
  // This api version is needed for agent 5 endpoints
  private readonly apiVersion: string = `v1/ad/`;
  private readonly coreVersion: string = `v1/core/`;

  // shared helper variable to track opened tooltips in the tables
  currentTooltip: HTMLElement | null = null;

  constructor(private apiService: ApiService) {}

  /**
   * FS tab functions
   */

  postDocumentToFSChecks(file: File): Observable<any> {
    const slug: string = `${this.apiVersion}` + `fscheck`;
    const formData = new FormData();
    formData.append('file', file);

    return this.apiService.post(slug, formData, '');
  }

  getFSCheckLatestId(): Observable<any> {
    const slug: string = `${this.apiVersion}` + `fscheck`;
    return this.apiService.get(slug, { limit: 1 }, '');
  }

  getFSChecksResult(projectId: string): Observable<any> {
    const slug: string = `${this.apiVersion}` + `fscheck/${projectId}/result`;
    return this.apiService.get(slug, {}, '');
  }

  // as PDF
  getFSCheckExported(projectId: string | any): Observable<any> {
    const slug: string = `${this.apiVersion}` + `fscheck/${projectId}/export`;
    return this.apiService.get(slug, {}, '', {
      observe: 'response',
      responseType: 'blob',
    });
  }

  // as excel
  getFSCheckExportedAsExcel(projectId: string | any): Observable<any> {
    const slug: string =
      `${this.apiVersion}` + `fscheck/${projectId}/export/xlsx`;
    return this.apiService.get(slug, {}, '', {
      observe: 'response',
      responseType: 'blob',
    });
  }

  patchFSCheckLastExportedAt(projectId: string): Observable<any> {
    const slug: string = `${this.apiVersion}` + `fscheck/${projectId}/export`;
    return this.apiService.patch(slug, '', {}, '');
  }

  /**
   * FS vs Comp tab functions
   */

  postDocumentsToFSvsComp(files: File[]) {
    const slug: string = `${this.apiVersion}` + `fsvcomp`;
    const formData = new FormData();
    formData.append('file_fs', files[0]);
    formData.append('file_comp', files[1]);

    return this.apiService.post(slug, formData, '');
  }

  getFSvsCompLatestId(): Observable<any> {
    const slug: string = `${this.apiVersion}` + `fsvcomp`;
    return this.apiService.get(slug, { limit: 1 }, '');
  }

  getFSvsCompResult(projectId: string): Observable<any> {
    const slug: string = `${this.apiVersion}` + `fsvcomp/${projectId}/result`;
    return this.apiService.get(slug, {}, '');
  }

  // as PDF
  getFSvsCompExported(projectId: string | any): Observable<any> {
    const slug: string = `${this.apiVersion}` + `fsvcomp/${projectId}/export`;
    return this.apiService.get(slug, {}, '', {
      observe: 'response',
      responseType: 'blob',
    });
  }

  // as excel
  getFSvsCompExportedAsExcel(projectId: string | any): Observable<any> {
    const slug: string =
      `${this.apiVersion}` + `fsvcomp/${projectId}/export/xlsx`;
    return this.apiService.get(slug, {}, '', {
      observe: 'response',
      responseType: 'blob',
    });
  }

  patchFSvsCompLastExportedAt(projectId: string): Observable<any> {
    const slug: string = `${this.apiVersion}` + `fsvcomp/${projectId}/export`;
    return this.apiService.patch(slug, '', {}, '');
  }

  /**
   * CY vs PY tab functions
   */

  postDocumentsToCYvsPY(files: File[]) {
    const slug: string = `${this.apiVersion}` + `cyvpy`;
    const formData = new FormData();
    formData.append('file_cy', files[0]);
    formData.append('file_py', files[1]);

    return this.apiService.post(slug, formData, '');
  }

  getCYvsPYLatestId(): Observable<any> {
    const slug: string = `${this.apiVersion}` + `cyvpy`;
    return this.apiService.get(slug, { limit: 1 }, '');
  }

  getCYvsPYResult(projectId: string): Observable<any> {
    const slug: string = `${this.apiVersion}` + `cyvpy/${projectId}/result`;
    return this.apiService.get(slug, {}, '');
  }

  // as PDF
  getCYvsPYExported(projectId: string | any): Observable<any> {
    const slug: string = `${this.apiVersion}` + `cyvpy/${projectId}/export`;
    return this.apiService.get(slug, {}, '', {
      observe: 'response',
      responseType: 'blob',
    });
  }

  // as excel
  getCYvsPYExportedAsExcel(projectId: string | any): Observable<any> {
    const slug: string =
      `${this.apiVersion}` + `cyvpy/${projectId}/export/xlsx`;
    return this.apiService.get(slug, {}, '', {
      observe: 'response',
      responseType: 'blob',
    });
  }

  // as PDF
  getCYvsPYAdjExported(projectId: string | any): Observable<any> {
    const slug: string =
      `${this.apiVersion}` + `cyvpy/${projectId}/export-adjustments`;
    return this.apiService.get(slug, {}, '', {
      observe: 'response',
      responseType: 'blob',
    });
  }

  // as excel
  getCYvsPYAdjExportedAsExcel(projectId: string | any): Observable<any> {
    const slug: string =
      `${this.apiVersion}` + `cyvpy/${projectId}/export-adjustments/xlsx`;
    return this.apiService.get(slug, {}, '', {
      observe: 'response',
      responseType: 'blob',
    });
  }

  patchCYvsPYAdjLastExportedAt(projectId: string): Observable<any> {
    const slug: string =
      `${this.apiVersion}` + `cyvpy/${projectId}/export-adjustments`;
    return this.apiService.patch(slug, '', {}, '');
  }

  patchCYvsPYSuppLastExportedAt(projectId: string): Observable<any> {
    const slug: string = `${this.apiVersion}` + `cyvpy/${projectId}/export`;
    return this.apiService.patch(slug, '', {}, '');
  }

  /** General use function for this agent */
  getPDFPage(fileId: string | any): Observable<any> {
    const slug: string = `${this.coreVersion}` + `download/${fileId}`;
    return this.apiService.get(slug, {}, '', { responseType: 'blob' });
  }
}
